import { Workbook, Worksheet } from 'exceljs';
import { ContractReleaseRow_Processed } from './contractReleaseTypes';

/**
 * Creates a workbook with Contract Release data and a Summary pivot table
 * @param dataRows The processed contract release data rows
 * @param date Optional date for the worksheet name, defaults to current date
 * @returns A new Workbook with the data and pivot table
 */
export function createContractReleaseWorkbook(dataRows: ContractReleaseRow_Processed[], date?: Date): Workbook {
  // Create new workbook for the report
  const reportWorkbook = new Workbook();

  // Format the date for the worksheet name
  const formattedDate = formatDateForWorksheet(date || new Date());

  // Create Summary worksheet with pivot table
  const summarySheet = reportWorkbook.addWorksheet('Summary');

  // Create Contract Release worksheet with the formatted date
  const contractReleaseSheet = reportWorkbook.addWorksheet(`Contract Release ${formattedDate}`);

  // Set default row height for Arial 10 font
  summarySheet.properties.defaultRowHeight = 13.5;
  contractReleaseSheet.properties.defaultRowHeight = 13.5;

  // Define columns based on sample.csv with Arial 10 font
  contractReleaseSheet.columns = [
    { header: 'Location', key: 'location', width: 15, style: { font: { name: 'Arial', size: 10 } } },
    { header: 'P/S/O', key: 'pso', width: 10, style: { font: { name: 'Arial', size: 10 } } },
    { header: 'Cont', key: 'cont', width: 10, style: { font: { name: 'Arial', size: 10 } } },
    { header: 'Contract No', key: 'contractNo', width: 12, style: { font: { name: 'Arial', size: 10 } } },
    { header: 'Status', key: 'status', width: 10, style: { font: { name: 'Arial', size: 10 } } },
    { header: 'Name', key: 'name', width: 25, style: { font: { name: 'Arial', size: 10 } } },
    { header: 'Commodity', key: 'commodity', width: 12, style: { font: { name: 'Arial', size: 10 } } },
    { header: 'Commodity Description', key: 'commodityDescription', width: 25, style: { font: { name: 'Arial', size: 10 } } },
    { header: 'Rel', key: 'rel', width: 8, style: { font: { name: 'Arial', size: 10 } } },
    { header: 'UOM', key: 'uom', width: 8, style: { font: { name: 'Arial', size: 10 } } },
    { header: 'Quantity', key: 'quantity', width: 12, style: { font: { name: 'Arial', size: 10 }, numFmt: '#,##0.######' } },
    { header: 'Balance', key: 'balance', width: 12, style: { font: { name: 'Arial', size: 10 }, numFmt: '#,##0.######' } },
    { header: 'Add/Ded Rate Type', key: 'addDedRateType', width: 18, style: { font: { name: 'Arial', size: 10 } } },
    { header: 'Add/Ded Code 1', key: 'addDedCode1', width: 15, style: { font: { name: 'Arial', size: 10 } } },
    { header: 'Add/Ded Adjustment 1', key: 'addDedAdjustment1', width: 20, style: { font: { name: 'Arial', size: 10 } } },
    { header: 'Add/Ded Amount 1', key: 'addDedAmount1', width: 15, style: { font: { name: 'Arial', size: 10 }, numFmt: '#,##0.######' } },
    { header: 'Add/Ded Code 2', key: 'addDedCode2', width: 15, style: { font: { name: 'Arial', size: 10 } } },
    { header: 'Add/Ded Adjustment 2', key: 'addDedAdjustment2', width: 20, style: { font: { name: 'Arial', size: 10 } } },
    { header: 'Add/Ded Amount 2', key: 'addDedAmount2', width: 15, style: { font: { name: 'Arial', size: 10 }, numFmt: '#,##0.######' } },
    { header: 'OTC Fee Premium', key: 'otcFeePremium', width: 15, style: { font: { name: 'Arial', size: 10 }, numFmt: '#,##0.######' } }
  ];

  // Add all data rows to the Contract Release worksheet
  dataRows.forEach(row => {
    contractReleaseSheet.addRow({
      location: row.location,
      pso: row.pso,
      cont: row.cont,
      contractNo: row.contractNo,
      status: row.status,
      name: row.name,
      commodity: row.commodity,
      commodityDescription: row.commodityDescription,
      rel: row.rel,
      uom: row.uom,
      quantity: row.quantity,
      balance: row.balance,
      addDedRateType: row.addDedRateType,
      addDedCode1: row.addDedCode1,
      addDedAdjustment1: row.addDedAdjustment1,
      addDedAmount1: row.addDedAmount1,
      addDedCode2: row.addDedCode2,
      addDedAdjustment2: row.addDedAdjustment2,
      addDedAmount2: row.addDedAmount2,
      otcFeePremium: row.otcFeePremium
    });
  });

  // Freeze the header row
  contractReleaseSheet.views = [
    { state: 'frozen', xSplit: 0, ySplit: 1 }
  ];

  // Create a simple pivot table representation
  // Note: ExcelJS doesn't fully support creating pivot tables programmatically
  // This creates a static representation of what the pivot table would look like

  // Group data by Location and Commodity
  const pivotData = new Map<string, Map<string, number>>();

  // Process data for the pivot table
  dataRows.forEach(row => {
    const location = row.location;
    const commodity = row.commodity;
    const otcFeePremium = row.otcFeePremium;

    if (!pivotData.has(location)) {
      pivotData.set(location, new Map<string, number>());
    }

    const locationMap = pivotData.get(location)!;
    const currentValue = locationMap.get(commodity) || 0;
    locationMap.set(commodity, currentValue + otcFeePremium);
  });

  // Set up the pivot table headers with Arial 10 font
  summarySheet.columns = [
    { header: 'Location', key: 'location', width: 20, style: { font: { name: 'Arial', size: 10 } } },
    { header: 'Commodity', key: 'commodity', width: 15, style: { font: { name: 'Arial', size: 10 } } },
    { header: 'Sum of OTC Fee Premium', key: 'otcFeePremium', width: 25, style: { font: { name: 'Arial', size: 10 }, numFmt: '_($* #,##0.######_);_($* (#,##0.######);_($* "-"??_);_(@_)' } }
  ];

  // Add the pivot data
  let rowIndex = 2; // Start after header
  let grandTotal = 0;

  // Add data rows grouped by location and commodity
  for (const [location, commodities] of pivotData.entries()) {
    let locationSum = 0;

    for (const [commodity, sum] of commodities.entries()) {
      summarySheet.getCell(`A${rowIndex}`).value = location;
      summarySheet.getCell(`B${rowIndex}`).value = commodity;
      summarySheet.getCell(`C${rowIndex}`).value = sum;

      locationSum += sum;
      grandTotal += sum;
      rowIndex++;
    }

    // Add location subtotal
    summarySheet.getCell(`A${rowIndex}`).value = location + ' Total';
    summarySheet.getCell(`C${rowIndex}`).value = locationSum;

    // Apply bold formatting to the total row while keeping Arial 10 font
    ['A', 'B', 'C'].forEach(col => {
      const cell = summarySheet.getCell(`${col}${rowIndex}`);
      cell.font = { name: 'Arial', size: 10, bold: true };
    });

    rowIndex++;
  }

  // Add grand total row
  summarySheet.getCell(`A${rowIndex}`).value = 'Grand Total';
  summarySheet.getCell(`C${rowIndex}`).value = grandTotal;

  // Apply bold formatting to the grand total row while keeping Arial 10 font
  ['A', 'B', 'C'].forEach(col => {
    const cell = summarySheet.getCell(`${col}${rowIndex}`);
    cell.font = { name: 'Arial', size: 10, bold: true };
  });

  // Add metadata to indicate this is a pivot table
  summarySheet.getCell('A1').note = {
    texts: [{
      text: 'This sheet represents a pivot table with Location and Commodity as rows and Sum of OTC Fee Premium as values.'
    }]
  };

  return reportWorkbook;
}

/**
 * Formats a date as MM.DD.YY (e.g., 07.21.25)
 * @param date The date to format
 * @returns Formatted date string
 */
function formatDateForWorksheet(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);

  return `${month}.${day}.${year}`;
}
